import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import BackToHome from '../../components/feature/BackToHome';
import RevealStagger from '../../components/feature/RevealStagger';
import { fetchContactPage, type ContactPage } from '../../services/contactPage';
import Seo, { buildBreadcrumbJsonLd, SITE_URL } from '../../components/feature/Seo';

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [pageData, setPageData] = useState<ContactPage | null>(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    type: searchParams.get('type') === 'private' ? 'private' : 'business'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchContactPage();
        setPageData(data);
      } catch (error) {
        console.error('Failed to load contact page:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/send-contact-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          type: 'business'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const seoTitle = pageData?.heading
    ? `${pageData.heading} | Gerald Fasching`
    : 'Kontakt - Gerald Fasching | Business & Personal Coaching, Mentoring';
  const seoDescription =
    pageData?.subheading ||
    'Nehmen Sie Kontakt mit Gerald Fasching auf - für Business Coaching, Personal Coaching, Führungskräfte-Training, Mentoring und alchemistische Kamingespräche. Standort Winklarn/Amstetten, Niederösterreich.';
  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    url: `${SITE_URL}/kontakt`,
    name: seoTitle,
    description: seoDescription,
    mainEntity: {
      '@type': 'Person',
      '@id': `${SITE_URL}/#person`,
      name: 'Gerald Fasching',
      telephone: pageData?.phone || '+43 664 325 8431',
      email: pageData?.email || 'office@gerald-fasching.at',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Harterstraße 14',
        postalCode: '3300',
        addressLocality: 'Winklarn/Amstetten',
        addressCountry: 'AT',
      },
    },
  };
  const seo = (
    <Seo
      title={seoTitle}
      description={seoDescription}
      path="/kontakt"
      jsonLd={[
        contactJsonLd,
        buildBreadcrumbJsonLd([
          { name: 'Start', path: '/' },
          { name: 'Kontakt', path: '/kontakt' },
        ]),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <LoadingSpinner bgColor="bg-white" />
      </>
    );
  }

  const heading = pageData?.heading || 'Kontakt';
  const subheading = pageData?.subheading || '';
  const contactInfoHeading = pageData?.contactInfoHeading || 'Direkte Kontaktmöglichkeiten';
  const phone = pageData?.phone || '+43 664 325 8431';
  const email = pageData?.email || 'office@gerald-fasching.at';
  const address = pageData?.address || 'Harterstraße 14, 3300 Winklarn/Amstetten, Österreich';
  const formHeading = pageData?.formHeading || 'Nachricht senden';
  const mapEmbedUrl = pageData?.mapEmbedUrl || 'https://maps.google.com/maps?q=Harterstra%C3%9Fe+14%2C+3300+Amstetten%2C+%C3%96sterreich&t=&z=14&ie=UTF8&iwloc=&output=embed';

  return (
    <div className="min-h-screen bg-white">
      {seo}

      {/* Hero Section */}
      <section className="px-6 pb-10 pt-28 lg:pb-20 lg:pt-32">
        <div className="mx-auto max-w-7xl">
          <BackToHome />
          <RevealStagger animateOnMount className="mx-auto mb-16 max-w-3xl text-center">
            <h1 className="text-[1.75rem] md:text-[2.125rem] lg:text-[2.75rem] leading-snug font-bold text-gray-900 mb-6" style={{ fontFamily: 'Lora, serif' }}>
              {heading}
            </h1>
            {subheading && (
              <p className="text-base lg:text-xl text-gray-600 leading-relaxed">
                {subheading}
              </p>
            )}
          </RevealStagger>

          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* Contact Information */}
            <div className="space-y-12">
              <div>
                <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Lora, serif' }}>
                  {contactInfoHeading}
                </h2>

                <RevealStagger className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-900 to-red-800 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                      <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-sm lg:text-base text-gray-600 hover:text-red-900 transition-colors">
                        {phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">E-Mail</h3>
                      <a href={`mailto:${email}`} className="text-sm lg:text-base text-gray-600 hover:text-red-900 transition-colors">
                        {email}
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-amber-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Standort</h3>
                      <p className="text-sm lg:text-base text-gray-600">
                        {address}
                      </p>
                    </div>
                  </div>
                </RevealStagger>
              </div>
            </div>

            {/* Contact Form */}
            <RevealStagger className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Lora, serif' }}>
                {formHeading}
              </h2>

              <form onSubmit={handleSubmit} id="contact-form" className="space-y-6">
                {/* Type Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Ich interessiere mich für
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'business' }))}
                      className={`cursor-pointer whitespace-nowrap rounded-lg border px-4 py-3 text-sm font-semibold transition-all ${
                        formData.type === 'business'
                          ? 'border-[#8B1538] bg-[#8B1538]/8 text-[#8B1538]'
                          : 'border-[#1A2B3C]/15 text-[#1A2B3C]/70 hover:border-[#1A2B3C]/30 hover:text-[#1A2B3C]'
                      }`}
                    >
                      Business
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'private' }))}
                      className={`cursor-pointer whitespace-nowrap rounded-lg border px-4 py-3 text-sm font-semibold transition-all ${
                        formData.type === 'private'
                          ? 'border-[#8B1538] bg-[#8B1538]/8 text-[#8B1538]'
                          : 'border-[#1A2B3C]/15 text-[#1A2B3C]/70 hover:border-[#1A2B3C]/30 hover:text-[#1A2B3C]'
                      }`}
                    >
                      Privat
                    </button>
                  </div>
                  <input type="hidden" name="type" value={formData.type} />
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 outline-none transition-all text-sm"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 outline-none transition-all text-sm"
                    placeholder="ihre.email@beispiel.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 outline-none transition-all text-sm"
                    placeholder="+43 123 456 789"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                    Betreff *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 outline-none transition-all text-sm"
                    placeholder="Worum geht es?"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-red-900 focus:ring-2 focus:ring-red-900/20 outline-none transition-all resize-none text-sm"
                    placeholder="Teilen Sie mir mit, wie ich Ihnen helfen kann..."
                  />
                  <p className="text-xs lg:text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 Zeichen
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-[#8B1538] px-7 py-3.5 text-sm font-semibold leading-none text-white shadow-lg shadow-[#8B1538]/25 transition-all hover:bg-[#6B0F2A] hover:shadow-xl active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#8B1538] disabled:hover:shadow-lg"
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    {pageData?.successMessage || 'Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Ich melde mich in Kürze bei Ihnen.'}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    {pageData?.errorMessage || 'Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut oder kontaktieren Sie mich direkt.'}
                  </div>
                )}
              </form>
            </RevealStagger>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 px-6 py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Lora, serif' }}>
              Standort
            </h2>
            <p className="text-sm lg:text-lg text-gray-600">
              {address}
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-lg h-96">
            <iframe
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
